import { useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { NavLink } from 'react-router-dom';
import { createHttpClient } from '@shared/api/httpClient';
import { canDownloadClientArtifacts, getFriendlyUserRole, getSignedInUserDisplayName, isClientAdmin, signOut } from '@shared/auth/cognito';
import { useClientAiAssistant } from './ClientAiAssistantDrawer';
import { useClientAccount } from '../account/AccountContext';
import surelincLogo from '../assets/surelinc-logo.png';
import type { ClientUsersManagementAccessResponse } from '@shared/types/api';

type NavIconName = 'dashboard' | 'approval' | 'packages' | 'workspace' | 'actions' | 'reports' | 'signout' | 'user' | 'spark';

function ShellIcon({ name, className = '' }: { name: NavIconName; className?: string }) {
  const common = {
    className: `shell-icon ${className}`.trim(),
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  switch (name) {
    case 'dashboard':
      return <svg {...common}><path d="M4 11l8-7 8 7" /><path d="M6 10v10h12V10" /><path d="M10 20v-6h4v6" /></svg>;
    case 'approval':
      return <svg {...common}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>;
    case 'packages':
      return <svg {...common}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M8 13h8" /><path d="M8 17h5" /></svg>;
    case 'workspace':
      return <svg {...common}><path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 14l2 2 4-5" /></svg>;
    case 'actions':
      return <svg {...common}><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></svg>;
    case 'reports':
      return <svg {...common}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M9 15h6" /><path d="M9 11h2" /></svg>;
    case 'signout':
      return <svg {...common}><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="M10 17l5-5-5-5" /><path d="M15 12H3" /></svg>;
    case 'user':
      return <svg {...common}><path d="M20 21a8 8 0 0 0-16 0" /><circle cx="12" cy="7" r="4" /></svg>;
    case 'spark':
      return <svg {...common}><path d="M12 3l1.7 5.1L19 10l-5.3 1.9L12 17l-1.7-5.1L5 10l5.3-1.9z" /><path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z" /></svg>;
  }
}

const navItems: Array<{ path: string; label: string; icon: NavIconName; end?: boolean }> = [
  { path: '/dashboard', label: 'Dashboard', icon: 'dashboard', end: true },
  { path: '/approvals', label: 'Approvals', icon: 'approval' },
  { path: '/evidence-packages', label: 'Evidence Packages', icon: 'packages' },
  { path: '/evidence-workspace', label: 'Evidence Workspace', icon: 'workspace' },
  { path: '/actions-required', label: 'Actions Required', icon: 'actions' },
  { path: '/reports', label: 'Reports', icon: 'reports' },
];

export default function Layout({ children }: PropsWithChildren) {
  const friendlyRole = getFriendlyUserRole();
  const signedInDisplayName = getSignedInUserDisplayName(friendlyRole);
  const canDownload = canDownloadClientArtifacts();
  const clientAdmin = isClientAdmin();
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const [usersNavEnabled, setUsersNavEnabled] = useState(false);
  const { openDrawer } = useClientAiAssistant();
  const { accounts, selectedAccount, selectAccount, accountPath } = useClientAccount();

  useEffect(() => {
    let cancelled = false;

    if (!clientAdmin) {
      setUsersNavEnabled(false);
      return () => {
        cancelled = true;
      };
    }

    api
      .get<ClientUsersManagementAccessResponse>('/client/users/access')
      .then((response) => {
        if (!cancelled) {
          setUsersNavEnabled(Boolean(response.enabled && response.canManage));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUsersNavEnabled(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, clientAdmin, selectedAccount?.accountId]);

  const visibleNavItems = usersNavEnabled
    ? [...navItems, { path: '/users', label: 'Users', icon: 'user' as NavIconName }]
    : navItems;

  return (
    <div className="app-shell">
      <aside className="sidebar" aria-label="Client portal navigation">
        <div className="brand client-brand">
          <img className="client-brand-logo" src={surelincLogo} alt="SURELINC" />
          <div className="client-brand-subtitle">Readiness Platform™</div>
        </div>

        <nav className="nav-list">
          {visibleNavItems.map((item) => {
            const to = accountPath(item.path);
            return (
              <NavLink
                key={item.path}
                to={to}
                end={item.end}
                className={({ isActive }) => (isActive ? 'nav-link nav-link-active' : 'nav-link')}
              >
                <ShellIcon name={item.icon} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div>
            <h1>SURELINC Readiness Platform™</h1>
            <p>{selectedAccount ? `${selectedAccount.displayName || selectedAccount.accountId} · AWS ${selectedAccount.accountId}` : 'Organization portal. Select an assigned AWS environment to continue.'}</p>
          </div>
          <div className="user-actions">
            {selectedAccount && (
              <label className="account-switcher">
                <span className="account-switcher-label">Selected AWS environment</span>
                <select
                  value={selectedAccount.accountId}
                  onChange={(event) => selectAccount(event.target.value)}
                  aria-label="Switch AWS environment"
                >
                  {accounts.map((account) => (
                    <option key={account.accountId} value={account.accountId}>
                      {account.displayName || account.accountId} · {account.accountId.slice(-4)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button
              className="secondary-button topbar-ai-button"
              type="button"
              onClick={() => openDrawer()}
              title="Open SURELINC Readiness AI Assistant™"
            >
              <ShellIcon name="spark" />
              <span>SURELINC Readiness AI Assistant™</span>
            </button>
            {signedInDisplayName && (
              <div className="signed-in-summary">
                <ShellIcon name="user" className="signed-in-icon" />
                <div className="signed-in-copy">
                  <span className="signed-in-user">{signedInDisplayName}</span>
                  <span
                    className={`signed-in-role ${canDownload ? 'signed-in-role-admin' : 'signed-in-role-readonly'}`}
                    title={canDownload ? 'This role can download authorized client artifacts.' : 'View-only role. Downloads are admin-only.'}
                  >
                    {friendlyRole}
                  </span>
                </div>
              </div>
            )}
            <button className="secondary-button signout-button" type="button" onClick={signOut}>Sign out <ShellIcon name="signout" /></button>
          </div>
        </header>

        <section className="page-content">{children}</section>
      </main>
    </div>
  );
}
